import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { DatabaseWrapper, TransactionId } from 'services/database-wrapper';
import { Mutex } from '@cf-shared/mutex';
import { deepOverwriteCouchSafe } from '@cf-shared/deep-helpers';
import { getEntryLocalTimeInDesiredTimeZone, LogRecordType, Log_Entry } from '@cf-shared/data-model/user-log';
import {
  DayTaskStatusCache,
  DayTaskStatuses,
  DAY_TASK_STATUS_CACHE_ID,
  TaskStatus,
} from '@cf-shared/data-model/day-task-status-cache';
import { DayTaskStatusCacheDoc } from './day-task-status-cache-doc';
import { LoggingService, errorReportFields } from './logging.service';
import { PubSubService } from './pubsub.service';
import { DebugService } from 'services/debug.service';
import { DatabaseUnavailableError, isDatabaseUnavailable, isDatabaseTornDown, isDatabaseRecovering } from './database-unavailable-error';
import { TaskStatusCacheUpdatedEvent } from './user-db.service';
import {
  applyStatusesToMemoWithoutDowngrade,
  chooseDayStatusSource,
  DayStatusSource,
  buildDayStatusesForRange,
  computeTaskStatusesWithSavedEntries,
  describeMissingSavedEntries,
  deriveAffectedDatesFromPulledDocs,
  groupTaskStatusRowsByDate,
  PulledLogDoc,
  REACTIVE_STATUS_DEBOUNCE_MS,
  REACTIVE_STATUS_HARD_CAP_MS,
  REACTIVE_STATUS_CATCHUP_DEBOUNCE_MS,
  REACTIVE_STATUS_CATCHUP_HARD_CAP_MS,
  ReactiveDayStatusAction,
  ReactiveDayStatusCoalescer,
  ReactiveDayStatusDecision,
  reactiveStatusRetryDelayMs,
  logDocsToTaskStatusRows,
  planCacheLoad,
  reconcileViewRowsWithLogDocs,
  TaskStatusQueryRow,
  TaskStatusReadPhase,
  TaskStatusRowSource,
  TaskStatusViewCheck,
  TaskStatusViewSampler,
  decideUnavailableRawReadOutcome,
  UnavailableRawReadOutcome,
  runOnInitialSyncSuccess,
  widenTaskStatusRange,
  taskStatusIdRangeBounds,
  isReactiveFlushAbandoned,
  ReactiveFlushAbandonReason,
  ReactiveFlushToken,
} from './user-db.task-status';

const dayTaskStatusCacheId = DAY_TASK_STATUS_CACHE_ID;

/** Did the view-independent raw-docs read run, or not? Used as the discriminant
 *  of `RawTaskStatusDocsReadOutcome` below. An enum rather than a boolean per
 *  CLAUDE.md — and because a boolean discriminant was tried here first and did
 *  NOT narrow: `if (!outcome.available) return outcome.result;` failed to
 *  compile with TS2339 in this project's config. */
enum RawDocsReadStatus {
  Available = 'available',
  Unavailable = 'unavailable',
}

/**
 * One answer from `readTaskStatusRowsAsync`.
 *
 * `verified` marks rows that could not be checked against the raw docs — display
 * them, never persist them (#4219). `rowsCameFrom` says which source produced them
 * (#4350): since #4249 Phase 3 most post-sync reads never consult the view, so a
 * report about a read that missed something must say what it actually read.
 */
interface TaskStatusRowsRead {
  rows: TaskStatusQueryRow[];
  verified: boolean;
  rowsCameFrom: TaskStatusRowSource;
}

/**
 * What `readRawDocsOrHandleUnavailableAsync` found (#4249 Phase 3).
 *
 * `Unavailable` carries the finished answer the read path must hand back —
 * either `null` ("do not persist anything", the #4212 signal) or the view's own
 * rows marked unverified.
 */
type RawTaskStatusDocsReadOutcome =
  | { status: RawDocsReadStatus.Available; logDocs: Log_Entry[] }
  | { status: RawDocsReadStatus.Unavailable; result: TaskStatusRowsRead | null };

/**
 * The day-task-status cache and the reactive engine that keeps it current.
 *
 * This used to live inside `UserDb`, where it was 1646 of that class's 4652 lines
 * and shared none of its data (#4249 built it there; df-3b8918aefa6a moved it out).
 * The two have nothing in common: `UserDb` stores the user's documents, and this
 * class owns one cache document plus the machinery that decides when to recompute
 * it. Neither reads the other's state.
 *
 * `UserDb` still owns the DATABASE, and hands this service each new one through
 * `setDatabase`. That is not a detail — it is the thing the generation counter
 * below defends against. See `resetReactiveDayStatusState`.
 *
 * The pure, stateless half of this subsystem lives in `user-db.task-status.ts`.
 * This class is the stateful orchestration that drives it.
 */
@Injectable()
export class DayTaskStatusService {
  // The database the cache is read from and written to. NOT injected: `UserDb`
  // replaces its own wrapper whenever the user database is (re)opened or switched,
  // and hands the new one over here at the same moment.
  //
  // It is null before the first init, and only then. Shutdown deliberately leaves
  // the shut-down wrapper in place rather than nulling it, exactly as `UserDb` does
  // with its own field and for the same reason: in-flight async callers would crash
  // on a null, whereas the wrapper's own internals are already nulled so their calls
  // safely no-op. Do not "tidy" this by calling `setDatabase(null)` at shutdown.
  private database: DatabaseWrapper = null;

  // serialize task-status-cache load-modify-save to prevent concurrent save conflicts
  private taskStatusCacheMutex = new Mutex();

  // #4249 reactive per-day status updates. The coalescer collects the calendar
  // days that pulled log docs changed; this service owns the timers, the reads
  // and the cache writes. See ReactiveDayStatusCoalescer for the why.
  private reactiveDayStatusCoalescer = new ReactiveDayStatusCoalescer();
  private reactiveStatusListener: { destroy: () => void } | null = null;
  private reactiveStatusDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private reactiveStatusHardCapTimer: ReturnType<typeof setTimeout> | null = null;
  // true while a reactive flush is running, so a timer that fires mid-flush does
  // not start a second overlapping recompute
  private reactiveStatusFlushRunning = false;
  // bumped whenever the reactive state is reset (user-database init or switch,
  // teardown). A flush that STARTED before the bump is holding dates that belong
  // to the previous database, so after each await it must check the generation
  // and abandon its results rather than requeueing or persisting them — writing
  // one user's statuses into another user's cache is the exact accident the
  // reset exists to prevent. Same idea as displayOnlyMemoEpoch.
  //
  // The three methods that do the flush's reads and writes route every await
  // through a ReactiveFlushToken, which does the comparison (df-44348d1531e4).
  // The few places that still compare by hand are pinned by the "cross-user-write
  // generation rule is checked" block in user-db.task-status.test.ts, and the
  // behaviour itself by day-task-status.generation.test.ts.
  private reactiveStatusGeneration = 0;
  // consecutive flushes that could not be trusted. Drives the retry backoff, so
  // a persistently wedged read cannot spin at the debounce interval forever.
  private reactiveStatusConsecutiveFailures = 0;
  // the failure count the currently-armed retry timer was armed for. Lets the
  // failure branch tell "the streak grew, this timer's delay is now wrong" from
  // "another batch arrived, leave the pending retry alone" — see
  // armReactiveStatusTimers.
  private reactiveStatusRetryArmedForFailures = -1;

  // set by a flush that put some of its dates back because the read could not
  // be trusted; drives the retry backoff in the caller's finally block
  private reactiveStatusFlushRequeued = false;

  // true while a post-catch-up flush of held reactive dates is armed, so
  // repeated Hold decisions don't stack duplicate waiters
  private reactiveStatusPostSyncFlushArmed = false;
  // how to stop the armed waiter, kept so a user-database switch can drop it
  private reactiveStatusPostSyncFlushStop: (() => void) | null = null;

  // true while a post-catch-up-sync calendar refresh is armed, so concurrent
  // degraded loads don't stack up duplicate one-shots (#4219)
  private postSyncTaskStatusRefreshArmed = false;
  // how to stop the armed waiter, kept so a user-database switch can drop it
  private postSyncTaskStatusRefreshStop: (() => void) | null = null;
  // the union of every day span armed while waiting, which is what the refresh
  // publishes. It is a union rather than the first span because subscribers skip
  // a reload that misses their visible days — see widenTaskStatusRange.
  private postSyncTaskStatusRefreshRange: { startDate: string; endDate: string } | null = null;

  // in-memory memo of the statuses computed by display-only load rounds while
  // the catch-up sync is running. NEVER persisted (that is the whole point of
  // display-only, #4219) — it only lets a later page entry render the calendar
  // instantly instead of waiting for a full recompute. Cleared when the user
  // database is (re)initialized, because its values belong to that database.
  private displayOnlyStatusMemo = new Map<string, DayTaskStatuses>();

  // bumped whenever memo contents are invalidated (user-database switch, or a
  // save-during-sync evicting the affected days). A reactive display refresh
  // whose read STARTED before the bump holds results that predate the
  // invalidating event — refreshDisplayOnlyStatusesAsync compares against this
  // and throws them away rather than writing them back into the just-cleaned
  // memo, where a pre-save snapshot would resurrect the evicted day and the
  // changed-only gate would then suppress the update event, hiding the
  // just-saved entry.
  private displayOnlyMemoEpoch = 0;

  // #4249 Phase 3. Decides which post-sync task-status reads also run the
  // `logEntriesForTaskStatus` view query and compare it against the raw docs.
  // One sampler for the whole service on purpose: "one read in N" then counts
  // every calendar and every read path together. It holds no user data, so it is
  // never rebuilt — but `resetReactiveDayStatusState` DOES re-arm it, because a
  // replaced local database has an unknown view index and deserves the same
  // first-read check a fresh boot gets.
  private taskStatusViewSampler = new TaskStatusViewSampler();

  constructor(
    private logging: LoggingService,
    private pubSub: PubSubService,
    private debug: DebugService,
  ) { }

  /**
   * Hand over the database this service should read and write from.
   *
   * `UserDb` calls this at the exact point it assigns its own `this.database`, so
   * the two never disagree about which database is current. Everything that must
   * happen AROUND the swap — dropping the old pulled-doc listener first, clearing
   * the display memo after — stays at `UserDb`'s call sites in the order it has
   * always run in, because that order is what #4249 established.
   */
  setDatabase(database: DatabaseWrapper): void {
    this.database = database;
  }

  /**
   * Register the pulled-doc listener on a database.
   *
   * `UserDb` calls this from `adoptLoadedDatabase`, where the schedule watcher is registered
   * too. Both moved there from the singleton load in #4809, and the move is load-bearing: the
   * cold path starts syncing before the documents are loaded, so a listener registered during
   * the load would note days that the reset at the end of the hand-over then wipes. The old
   * listener is destroyed first, exactly as before.
   */
  registerPulledDocListener(database: DatabaseWrapper): void {
    this.reactiveStatusListener?.destroy();
    this.reactiveStatusListener = database.registerPulledDocListener('log/', (docs) => this.onLogDocsPulled(docs));
  }

  /**
   * Drop the pulled-doc listener without registering a new one.
   *
   * Called BEFORE a database swap and on shutdown. Keeping this separate from
   * `registerPulledDocListener` is what lets `UserDb` destroy the old wrapper's
   * listener before it swaps, which is the #4249 ordering rule.
   */
  destroyPulledDocListener(): void {
    this.reactiveStatusListener?.destroy();
    this.reactiveStatusListener = null;
  }

  /**
   * Throw away every memoized display-only status.
   *
   * Called after a database swap: the memo holds values computed from the PREVIOUS
   * database instance, and a different user or session must never be served them.
   * The epoch bump also makes any in-flight display refresh drop its results.
   */
  clearDisplayOnlyMemo(): void {
    this.displayOnlyStatusMemo.clear();
    this.displayOnlyMemoEpoch++;
  }

  /**
   * Get empty day task status cache document
   */
  getEmptyDayTaskStatusCache(): DayTaskStatusCache {
    // Return the empty cache structure directly
    return {
      _id: dayTaskStatusCacheId,
      statusesByDate: {},
      // version 8 (df-c685d916a013): one-time rebuild to discard days whose
      // workout, weigh-in or weekly check-up status counted an entry the day
      // list hides (an empty copy beside a finished one of the same type).
      // Those days were stored "incomplete" with a stamp later than the day,
      // so nothing else would ever recompute them.
      //
      // version 7 (#6281): one-time rebuild to discard days whose meal status
      // counted a meal the meal list hides (an empty copy under the same name as
      // a finished meal). Those days were stored "incomplete" with a stamp later
      // than the day, so nothing else would ever recompute them.
      //
      // version 6 (#6258): one-time rebuild to discard days left stale by a save
      // made during the catch-up sync. That save used to skip its cache write and
      // trust a reload to fix the day, but the reload serves any stored value that
      // is possible for its date. Such a day carries a stamp LATER than the day
      // itself, so it looks exactly like a correctly settled one, and only a wipe
      // can reach it.
      //
      // version 5 (#4694): one-time rebuild to discard days that were worked out
      // while the day was still running and then never re-checked, because the
      // stored value was wrong but not impossible. Those days carry no
      // `computedOn` stamp, so the new staleness rule cannot tell them from a
      // correctly settled day — wiping once is what repairs the customers who
      // are holding a wrong calendar dot today.
      //
      // version 4 (#4219): one-time rebuild to discard caches poisoned by a
      // PARTIALLY-lying (wedged) view index — the #4212 v3 rebuild only
      // guarded against a completely empty view answer, so it recomputed the
      // whole window through the wedged index and re-poisoned it
      version: 8
    } as DayTaskStatusCache;
  }

  /**
   * Load day task status cache singleton
   */
  async loadDayTaskStatusCacheAsync(): Promise<DayTaskStatusCache> {
    return this.database.withDocWatcher(watcher =>
      this.database.getSingleton(dayTaskStatusCacheId, watcher)) as DayTaskStatusCache;
  }

  /**
   * Save day task status cache singleton
   */
  async saveDayTaskStatusCacheAsync(transaction: TransactionId) {
    return this.database.saveSingletonAsync(transaction, dayTaskStatusCacheId);
  }

  /**
   * Get cached task statuses for a date range.
   * This is much faster than calculating from log entries each time.
   * Returns individual task statuses for each category.
   */
  async getCachedTaskStatusesAsync(startDate: DateTime, endDate: DateTime): Promise<Map<string, DayTaskStatuses>> {
    return this.taskStatusCacheMutex.exclusiveCall(() => this.getCachedTaskStatusesInternalAsync(startDate, endDate));
  }

  private async getCachedTaskStatusesInternalAsync(startDate: DateTime, endDate: DateTime): Promise<Map<string, DayTaskStatuses>> {
    // Load the cache singleton (always exists due to initialization)
    let cacheDoc = await this.loadDayTaskStatusCacheAsync();

    // decide what this load round may WRITE: while the initial (catch-up) sync
    // is incomplete the local data can be missing recent docs, so nothing
    // computed now may be persisted and the version wipe must wait (#4219)
    const emptyCache = this.getEmptyDayTaskStatusCache();
    const expectedVersion = emptyCache.version;
    const initialSyncCompleted = this.database.initialSyncCompletedState === true;
    const loadPlan = planCacheLoad(cacheDoc.version, expectedVersion, initialSyncCompleted);

    if (loadPlan.resetOutdatedCache) {
      console.log(`Day task status cache version mismatch (found: ${cacheDoc.version}, expected: ${expectedVersion}). Recreating cache...`);

      // Reset the cache with the new structure using deep overwrite
      deepOverwriteCouchSafe(cacheDoc, emptyCache);

      // Save the updated cache structure
      await this.saveDayTaskStatusCacheAsync(DatabaseWrapper.getTransactionId());
    } else if (loadPlan.versionMismatch) {
      // the version wipe was DEFERRED because the catch-up sync is running —
      // the outdated (possibly poisoned) cached values are being served as-is.
      // if every visible day is present in that cache, the missing-dates path
      // below never runs and would never arm the refresh, so the poison would
      // stay on screen until the next page entry. arm it here so the wipe and
      // recompute happen the moment the sync completes (#4219 review finding)
      this.armPostSyncTaskStatusRefresh(startDate, endDate);
    }

    // every read, write and removal of a day goes through this owner, which also
    // makes sure the document has a day map (df-827a7f3bf41e)
    const cache = new DayTaskStatusCacheDoc(cacheDoc);

    // Check if we have all dates in the cache
    const today = this.debug.getNowLocal().startOf('day');
    const result = new Map<string, DayTaskStatuses>();
    const missingDates: DateTime[] = [];

    let currentDate = startDate.startOf('day');
    while (currentDate <= endDate) {
      const dateKey = currentDate.toISODate();
      // #4249 Phase 2 (P3): while the catch-up sync runs, a memo value beats the
      // persisted one. The reactive flush keeps the memo current as documents
      // arrive; the cache doc is deliberately not written during catch-up, so it
      // is the stale side. Serving it first — which is what this loop used to do
      // — hid every reactive update on any device with a populated cache doc.
      const memoStatuses = this.displayOnlyStatusMemo.get(dateKey);
      // `allowPersist` IS "the initial sync has completed" (planCacheLoad sets it
      // from exactly that), which is the condition the memo-precedence rule
      // needs. Naming the coupling because it is implicit: if allowPersist ever
      // gains a second condition, this rule silently changes meaning.
      const source = chooseDayStatusSource(
        cache.getDay(dateKey),
        memoStatuses,
        dateKey,
        today,
        loadPlan.allowPersist,
      );
      if (source === DayStatusSource.Memo) {
        result.set(dateKey, memoStatuses);
      } else if (source === DayStatusSource.Cache) {
        result.set(dateKey, cache.getDay(dateKey));
      } else {
        missingDates.push(currentDate);
      }
      currentDate = currentDate.plus({ days: 1 });
    }

    // If we have missing dates, calculate them and update cache
    if (missingDates.length > 0) {
      // while the catch-up sync is running, computing the missing days is slow
      // (large ranges, db contended by the bulk pull) and the result may not be
      // persisted. Days the memo already holds were served above by
      // chooseDayStatusSource, and the reactive flush keeps that memo fresh as
      // documents arrive (#4249 Phase 2) — nothing is revalidated in the
      // background any more.
      // NOTE: there is deliberately no "serve every missing day from the memo"
      // fast path here any more. It could not fire after Phase 2: mid-sync
      // `chooseDayStatusSource` already returns Memo for every day the memo
      // holds, so `missingDates` can only contain days the memo does NOT cover
      // — and a memo-covers-them-all check over those is false by construction.
      // The days that reach here genuinely have to be computed.

      const calculated = await this.calculateTaskStatusesForDatesAsync(
        missingDates[0],
        missingDates[missingDates.length - 1]
      );

      if (calculated == null) {
        // the query could not run (db recovering / shutting down) — serve the
        // stale cached values for the missing days instead of persisting wrong
        // data; the next load recomputes (#4212 defect D1)
        for (const date of missingDates) {
          const dateKey = date.toISODate();
          const cached = cache.getDay(dateKey);
          if (cached !== undefined) {
            result.set(dateKey, cached);
          }
        }
        return result;
      }

      // Which computed days may be SERVED.
      //
      // The recompute covers the whole span from the first missing day to the
      // last, so it also produces values for days this load already answered
      // from the memo or the cache. Whether those may be overwritten depends
      // entirely on whether this round is about to persist them:
      //
      //  - persisting round: the computed value is about to BECOME the cached
      //    value for every day in the span, so serving anything else would hand
      //    the caller a value this same call has just superseded.
      //  - display-only round: only the genuinely missing days may be taken.
      //    The others were answered by chooseDayStatusSource from the memo —
      //    which mid-sync is the fresher side, and is protected from
      //    downgrading — or from the cache doc. Neither is safe to overwrite
      //    with a value computed from data that has not finished arriving; the
      //    memo write below picks those days up for the next load anyway.
      const willPersist = loadPlan.allowPersist && calculated.verified;
      const missingDateKeys = new Set(missingDates.map(d => d.toISODate()));
      calculated.statuses.forEach((statuses, date) => {
        if (willPersist || missingDateKeys.has(date)) {
          result.set(date, statuses);
        }
      });

      if (willPersist) {
        // persist the computed statuses into the cache doc
        calculated.statuses.forEach((statuses, date) => {
          cache.putDay(date, statuses, today);
        });
        await this.saveDayTaskStatusCacheAsync(DatabaseWrapper.getTransactionId());
      } else {
        // display-only round: computed from possibly-incomplete data (catch-up
        // sync still running) or from an unverifiable view answer — never
        // persist those (#4219)
        console.log(`Task status load served display-only for ${startDate.toISODate()}..${endDate.toISODate()} (allowPersist=${loadPlan.allowPersist}, verified=${calculated.verified})`);
        // remember the computed values in the in-memory memo, so the next load
        // during this catch-up sync serves them instantly instead of making the
        // page wait for a full recompute (chooseDayStatusSource picks them up)
        if (!loadPlan.allowPersist) {
          // Never-downgrade: this round is mid-sync, so a day that computed to
          // nothing may simply not have arrived yet. Blanking it here would undo
          // on the load path the exact protection the reactive flush applies
          // (#4249 Phase 2).
          //
          // Restricted to the days that were actually MISSING, for a reason the
          // helper alone cannot cover. It compares a day against its previous
          // MEMO value, and a day answered from the cache doc has no memo entry
          // yet — so an empty recompute for it would sail through, and because a
          // memo value now beats the cache mid-sync, the next load would blank a
          // dot the cache was answering perfectly well. The recompute spans
          // first-missing..last-missing, so it sweeps up such days whenever the
          // missing ones are not contiguous. Those days keep their cached value
          // until the reactive flush or the post-sync one-shot updates them.
          const missingStatuses = new Map<string, DayTaskStatuses>();
          calculated.statuses.forEach((statuses, date) => {
            if (missingDateKeys.has(date)) {
              missingStatuses.set(date, statuses);
            }
          });
          applyStatusesToMemoWithoutDowngrade(this.displayOnlyStatusMemo, missingStatuses);
        }
        // if the catch-up sync is still running, arm a one-shot refresh so
        // open calendars recompute the moment it completes. do NOT arm in the
        // post-sync unverified case (allowPersist true, verified false): the
        // sync has already settled there, so the one-shot would fire straight
        // away (a late subscriber is answered with the outcome that already
        // happened), subscribers would reload, nothing would have been
        // persisted, and the cycle would repeat unbounded (review finding).
        // the unverified state resolves itself on the next natural load.
        if (!loadPlan.allowPersist) {
          this.armPostSyncTaskStatusRefresh(startDate, endDate);
        }
      }
    }

    return result;
  }

  /* ----------------------------------------------------------------------- *
   * #4249 — reactive per-day status updates for pulled log docs
   * ----------------------------------------------------------------------- */

  /**
   * A batch of `log/` docs arrived from another device. Work out which calendar
   * days they affect and hand those dates to the coalescer, which decides when
   * to recompute.
   *
   * Deliberately cheap: this runs inside the sync pull path, so it does string
   * and date work only — no reads, no logging per document (#4243).
   */
  private onLogDocsPulled(docs: PulledLogDoc[]): void {
    const today = this.debug.getNowLocal().startOf('day');
    // ids we could not map to any day are a real problem, not a normal skip:
    // for a body-less tombstone this is the only chance to learn the day, so a
    // dropped one leaves that dot stale with nothing to debug from (I6)
    const undecodableIds: string[] = [];
    const dates = deriveAffectedDatesFromPulledDocs(docs, today, undecodableIds);
    if (undecodableIds.length > 0) {
      this.logging.trackSilentProblem('TaskStatusPulledDocIdUndecodable', {
        count: undecodableIds.length,
        sampleId: undecodableIds[0],
      });
    }
    this.scheduleTaskStatusRecompute(dates);
  }

  /**
   * Hand these calendar days to the coalescer, so the reactive path recomputes
   * and persists them shortly.
   *
   * The one place that notes dates and arms the timers. It is called by
   * `onLogDocsPulled` above, and by the save-path branches that may not persist:
   * a save during the catch-up sync (#6258) and a save whose read could not be
   * trusted (#4694) — see `updateTaskStatusCacheForEntriesAsync`.
   */
  private scheduleTaskStatusRecompute(dateKeys: Iterable<string>): void {
    const { armTimer } = this.reactiveDayStatusCoalescer.noteDates([...dateKeys], Date.now());
    if (armTimer) {
      this.armReactiveStatusTimers();
    }
  }

  /**
   * (Re)arm the trailing debounce, and arm the hard cap once per pending wave.
   *
   * The debounce collapses one sync batch into one recompute. The hard cap
   * exists because a sustained pull stream keeps pushing the debounce out and
   * would otherwise never flush at all.
   */
  private armReactiveStatusTimers(): void {
    // raw setTimeout on purpose, not safeTimeout: safeTimeout is a page /
    // destroyGuard facility and UserDb is a long-lived service with neither.
    // These are not UI delays, and both handles are cleared by
    // clearReactiveStatusTimers on database init, switch and teardown — same
    // reasoning DatabaseWrapper's realtime-listener restart timer documents.
    // While backing off from a failed flush, ONE retry timer governs everything.
    // The debounce must not be re-armed per arriving doc here: a sustained pull
    // stream would keep postponing the retry forever — the same starvation the
    // hard cap exists to prevent, just moved. The retry forces its flush, so the
    // coalescer's debounce and hard cap cannot talk it out of running. Its
    // bounded-fallback interval still can (#4406), and that is deliberate: a
    // retry of a FAILED fallback is never held (the requeue clears the stamp),
    // while a retry that has meanwhile grown past the volume bound waits behind
    // a fallback that really did run. A held retry is not lost — the None branch
    // in runReactiveStatusFlushAsync re-arms for it.
    if (this.reactiveStatusConsecutiveFailures > 0) {
      // cancel any timer armed BEFORE the streak began, or it fires on the old
      // schedule and retries early — bypassing the backoff this branch exists to
      // apply. Without this, "one retry timer governs everything" is not true.
      if (this.reactiveStatusDebounceTimer != null) {
        clearTimeout(this.reactiveStatusDebounceTimer);
        this.reactiveStatusDebounceTimer = null;
      }
      // Replace an armed retry ONLY when the streak length it was armed for has
      // changed. Replacing it unconditionally would be the starvation trap
      // again, one level down: this method runs for every arriving batch, so a
      // stream landing faster than the backoff would clear and re-arm the retry
      // forever and it would never fire. Comparing against the count the live
      // timer was armed for fixes the case this is actually for — a doc arriving
      // MID-flush arms at count n, then the finally raises it to n+1 — while a
      // steady stream at an unchanged count leaves the pending retry alone.
      const armedForFailures = this.reactiveStatusRetryArmedForFailures;
      if (this.reactiveStatusHardCapTimer != null && armedForFailures === this.reactiveStatusConsecutiveFailures) {
        return;
      }
      if (this.reactiveStatusHardCapTimer != null) {
        clearTimeout(this.reactiveStatusHardCapTimer);
        this.reactiveStatusHardCapTimer = null;
      }
      this.reactiveStatusRetryArmedForFailures = this.reactiveStatusConsecutiveFailures;
      this.reactiveStatusHardCapTimer = setTimeout(() => {
        this.reactiveStatusHardCapTimer = null;
        void this.runReactiveStatusFlushAsync(true);
      }, reactiveStatusRetryDelayMs(this.reactiveStatusConsecutiveFailures));
      return;
    }

    // The catch-up sync gets its own, shorter pair. The coalescer applies the
    // same two constants when it decides whether a display tick may run, so if
    // the timers here stayed on the steady-state values the catch-up debounce
    // could never gate anything: the timer would not even fire until after it
    // had already elapsed. Both halves have to agree or the tunable is fiction —
    // which matters especially because PRD step 2.6 MEASURES these values.
    const initialSyncCompleted = this.database?.initialSyncCompletedState === true;
    const debounceMs = initialSyncCompleted ? REACTIVE_STATUS_DEBOUNCE_MS : REACTIVE_STATUS_CATCHUP_DEBOUNCE_MS;
    const hardCapMs = initialSyncCompleted ? REACTIVE_STATUS_HARD_CAP_MS : REACTIVE_STATUS_CATCHUP_HARD_CAP_MS;

    if (this.reactiveStatusDebounceTimer != null) {
      clearTimeout(this.reactiveStatusDebounceTimer);
    }
    this.reactiveStatusDebounceTimer = setTimeout(() => {
      this.reactiveStatusDebounceTimer = null;
      void this.runReactiveStatusFlushAsync(false);
    }, debounceMs);

    // the hard cap flushes a sustained stream that keeps pushing the debounce
    // out. It FORCES, so it also ends the case where the wall clock moved
    // backwards: the coalescer's own elapsed check can never come true then, and
    // the None branch would keep re-arming a timer that keeps answering None for
    // as long as the jump lasts.
    if (this.reactiveStatusHardCapTimer == null) {
      this.reactiveStatusHardCapTimer = setTimeout(() => {
        this.reactiveStatusHardCapTimer = null;
        void this.runReactiveStatusFlushAsync(true);
      }, hardCapMs);
    }
  }

  /**
   * Drop every pending reactive date and cancel both timers.
   *
   * Called at the same points that clear the display-only memo (user-database
   * init and switch) and at teardown. Same reasoning as the memo: the pending
   * dates describe one specific database, so carrying them across would
   * recompute one user's calendar against another user's data.
   *
   * Public because `UserDb` calls it at those points — it owns the database and
   * therefore knows when one is being replaced.
   */
  resetReactiveDayStatusState(): void {
    this.reactiveDayStatusCoalescer.reset();
    this.clearReactiveStatusTimers();
    // bump the generation LAST, after the pending set is empty: any flush still
    // in flight compares against this value after each await and abandons its
    // results instead of requeueing them or writing them into the new database
    this.reactiveStatusGeneration++;
    this.reactiveStatusPostSyncFlushArmed = false;
    // the waiter belongs to the database being replaced, so drop it rather than
    // leaving it subscribed to a wrapper nobody uses any more (#4435)
    if (this.reactiveStatusPostSyncFlushStop != null) {
      this.reactiveStatusPostSyncFlushStop();
      this.reactiveStatusPostSyncFlushStop = null;
    }
    // the calendar-refresh waiter is the same shape and belongs to the same
    // database, so it goes the same way. Its span describes the OLD user's
    // calendar, so drop that too rather than publishing it at the new one.
    this.postSyncTaskStatusRefreshArmed = false;
    if (this.postSyncTaskStatusRefreshStop != null) {
      this.postSyncTaskStatusRefreshStop();
      this.postSyncTaskStatusRefreshStop = null;
    }
    this.postSyncTaskStatusRefreshRange = null;
    this.reactiveStatusConsecutiveFailures = 0;
    this.reactiveStatusFlushRequeued = false;
    // the local database is being replaced, so the view index we would be
    // checking is a different one whose state is unknown — the same situation as
    // a fresh boot. Re-arm so the first read against it runs the cross-check
    // rather than possibly waiting N-1 reads (#4249 Phase 3).
    this.taskStatusViewSampler.rearm();
  }

  /**
   * A token for a flush that PERSISTS. It watches only the generation: a save
   * during the sync bumps the memo epoch, but that describes the display-only
   * memo, and a persisting flush reads verified data, so it must carry on.
   */
  private createPersistFlushToken(): ReactiveFlushToken {
    return new ReactiveFlushToken([
      { reason: ReactiveFlushAbandonReason.DatabaseSwitched, read: () => this.reactiveStatusGeneration },
    ]);
  }

  /**
   * A token for a display-only refresh. It watches the generation first and the
   * memo epoch second, so a tick where both moved reports the database switch.
   */
  private createDisplayFlushToken(): ReactiveFlushToken {
    return new ReactiveFlushToken([
      { reason: ReactiveFlushAbandonReason.DatabaseSwitched, read: () => this.reactiveStatusGeneration },
      { reason: ReactiveFlushAbandonReason.MemoInvalidated, read: () => this.displayOnlyMemoEpoch },
    ]);
  }

  private clearReactiveStatusTimers(): void {
    // forget which streak length the retry was armed for — the timer it
    // described is about to stop existing. Without this the value outlives its
    // timer, and a later streak whose first failure happens to match it would
    // take the early return in armReactiveStatusTimers and inherit whatever
    // ordinary hard cap was armed instead of a fresh backoff.
    this.reactiveStatusRetryArmedForFailures = -1;
    if (this.reactiveStatusDebounceTimer != null) {
      clearTimeout(this.reactiveStatusDebounceTimer);
      this.reactiveStatusDebounceTimer = null;
    }
    if (this.reactiveStatusHardCapTimer != null) {
      clearTimeout(this.reactiveStatusHardCapTimer);
      this.reactiveStatusHardCapTimer = null;
    }
  }

  /**
   * Flush any dates the reactive path is still holding, right now.
   *
   * Called from the app-resume path: a native suspend cancels both timers, so
   * without this the pending dates would sit unflushed until the next pulled
   * doc happened to arrive.
   */
  flushPendingReactiveStatusUpdates(): void {
    if (!this.reactiveDayStatusCoalescer.hasPending()) return;
    void this.runReactiveStatusFlushAsync(true);
  }

  /**
   * Ask the coalescer what to do with the pending dates, then do it.
   *
   * `force` skips the coalescer's wait check — used by the resume path and by
   * the post-sync flush of dates held during the catch-up sync.
   */
  private async runReactiveStatusFlushAsync(force: boolean): Promise<void> {
    // a timer that fires while a flush is still running must not start a second
    // overlapping recompute; the running flush re-checks for pending dates when
    // it finishes
    if (this.reactiveStatusFlushRunning) return;

    const initialSyncCompleted = this.database?.initialSyncCompletedState === true;
    const decision = this.reactiveDayStatusCoalescer.takeDecision(Date.now(), initialSyncCompleted, force);

    if (decision.action === ReactiveDayStatusAction.None) {
      // A timer fired but the coalescer is not ready yet — the wall clock moved
      // backwards (NTP correction, manual change, device resume), or it is
      // holding a bounded-fallback-sized wave on purpose (#4406). The timer that
      // fired has already cleared its own handle, so this re-arm is the only
      // thing that brings those dates back; it is what lets the coalescer refuse
      // even a FORCED call safely.
      if (this.reactiveDayStatusCoalescer.hasPending()) {
        this.armReactiveStatusTimers();
      }
      return;
    }

    if (decision.action === ReactiveDayStatusAction.Hold) {
      // the catch-up sync is still running: nothing computed now may be
      // persisted (I1). Hold the dates and flush them the moment the sync
      // completes — dropping them would leave those days stale indefinitely,
      // because a day already present in the cache doc is never recomputed on
      // its own.
      this.armReactiveStatusPostSyncFlush();
      return;
    }

    if (decision.action === ReactiveDayStatusAction.DisplayOnly) {
      // #4249 Phase 2: the sync is still running, so this recompute is for the
      // screen only. It still needs the post-sync flush armed — that is what
      // eventually PERSISTS these days from complete data.
      this.armReactiveStatusPostSyncFlush();
      // hold the SAME single-flight flag the persisting path uses. Two timers
      // can be armed at once (debounce + hard cap) and a resume can force a
      // third, so without this two display refreshes run against the same
      // database at once and an older read can land on top of a newer one —
      // concurrent recomputes in the exact window #4243 exists to keep quiet.
      this.reactiveStatusFlushRunning = true;
      try {
        await this.refreshDisplayOnlyStatusesAsync(this.createDisplayFlushToken(), decision);
      } catch (error) {
        // must reach a future debugger, same rule as the persisting path. Every
        // caller invokes this method as `void`, so without this a throw from the
        // local read is a silent unhandled rejection — and this is exactly the
        // condition the deleted TaskStatusRevalidateFailed used to report.
        // A wrapper that is being TORN DOWN is expected here, not a defect —
        // the refresh is fire-and-forget and there will be no next batch. Note
        // this is `isDatabaseTornDown`, NOT `isIntentionalDestruction()`: the
        // sign-out path shuts every wrapper down FIRST and only raises the wipe
        // flag later, so the flag is still false during the window that actually
        // produces these errors. A transient `Recovering` is excluded and still
        // reported, which is the whole reason for the narrower predicate.
        //
        // The console line moves with the report, not just the report: the E2E
        // gate fails on ANY console.error that is not allow-listed, and there is
        // deliberately no entry for this name. Same shape as `app-sentry.ts`'s
        // unhandled-rejection split and LoggingService's own IDB case.
        if (isDatabaseTornDown(error)) {
          console.log(`Reactive display-only status refresh skipped — our own teardown (${error.reason})`);
        } else {
          console.error('Reactive display-only status refresh failed:', error);
          this.logging.trackSilentProblem('TaskStatusDisplayRefreshFailed', errorReportFields(error));
        }
      } finally {
        this.reactiveStatusFlushRunning = false;
        // docs that arrived while this ran leave new display work behind
        if (this.reactiveDayStatusCoalescer.hasPending()) {
          this.armReactiveStatusTimers();
        }
      }
      return;
    }

    // this decision consumed the pending set, so both timers are now stale. A
    // leftover hard cap belongs to the wave that just flushed; left armed it
    // would fire during the NEXT wave and — because it forces — flush that wave
    // before its debounce window, throwing away the coalescing the 1000 ms
    // constant was measured to buy. The finally below re-arms if work remains.
    this.clearReactiveStatusTimers();

    // remember which database these dates belong to. A user-database switch
    // bumps the generation; the token makes the methods below stop at their next
    // await instead of writing anything back, and the two comparisons in the
    // catch and finally gate the side effects that must only happen while this
    // database is still current — see reactiveStatusGeneration.
    const generation = this.reactiveStatusGeneration;
    const flush = this.createPersistFlushToken();
    this.reactiveStatusFlushRunning = true;
    let flushSucceeded = false;
    try {
      if (decision.action === ReactiveDayStatusAction.FallbackRange) {
        await this.invalidateTaskStatusDatesAsync(flush, decision);
      } else {
        await this.recomputeAndPersistDatesAsync(flush, decision);
      }
      flushSucceeded = !this.reactiveStatusFlushRequeued;
    } catch (error) {
      // must reach a future debugger — same rule as the cache update path,
      // except for our own teardown, which the guard below splits off
      // same rule as the display refresh above, console line included. The
      // requeue below still runs either way, so no date is dropped — only the
      // reporting is suppressed, and only for our own teardown.
      //
      // An abandoned flush is split off FIRST: it is not a failure at all, so it
      // logs nothing and reports nothing, exactly as when an abandon returned
      // normally. It only ever means the generation moved, so the gates below
      // skip the requeue and the failure count for it too.
      if (!isReactiveFlushAbandoned(error)) {
        if (isDatabaseTornDown(error)) {
          console.log(`Reactive task status flush skipped — our own teardown (${error.reason})`);
        } else {
          console.error('Reactive task status flush failed:', error);
          this.logging.trackSilentProblem('TaskStatusReactiveFlushFailed', errorReportFields(error));
        }
      }
      // the dates were consumed by takeDecision, so put them back rather than
      // losing the change entirely — but only if they still belong to the
      // database we are pointing at
      if (generation === this.reactiveStatusGeneration) {
        this.reactiveDayStatusCoalescer.requeue(decision.dates, Date.now(), decision.action);
      }
    } finally {
      this.reactiveStatusFlushRunning = false;
      this.reactiveStatusFlushRequeued = false;
      // a switch happened while this flush ran — its dates are gone on purpose,
      // and re-arming here would resurrect work for the previous database
      if (generation === this.reactiveStatusGeneration) {
        this.reactiveStatusConsecutiveFailures = flushSucceeded ? 0 : this.reactiveStatusConsecutiveFailures + 1;
        // a requeue (failure) or docs that arrived during the flush leave work
        // behind — make sure a timer is armed for it
        if (this.reactiveDayStatusCoalescer.hasPending()) {
          this.armReactiveStatusTimers();
        }
      }
    }
  }

  /**
   * Recompute the given days for DISPLAY while the catch-up sync is running,
   * and write them into the memo (#4249 Phase 2, steps 2.1 + 2.2).
   *
   * Three rules make this safe, and each of them is load-bearing:
   *
   *  - **Local raw docs only.** The read goes through `getByIdRangeLocalOnlyAsync`,
   *    never the `logEntriesForTaskStatus` view and never a remote read. The view
   *    query is the expensive thing whose repetition froze the app in #4243, and
   *    a remote read would both cost a round trip mid-sync and graft-notify back
   *    into the sync machinery that triggered this.
   *  - **Nothing is persisted.** Results go to the memo only (I1). The post-sync
   *    one-shot recomputes these same days from complete data and persists then.
   *  - **A day never downgrades to empty.** Mid-sync an empty answer means "not
   *    arrived yet", not "no task" — see applyStatusesToMemoWithoutDowngrade.
   *
   * This cannot loop, which is the point of the whole phase: publishing reloads
   * open calendars, they serve from the memo (P3 precedence), and serving
   * schedules nothing. Only a newly pulled document can start another round.
   */
  private async refreshDisplayOnlyStatusesAsync(flush: ReactiveFlushToken, decision: ReactiveDayStatusDecision): Promise<void> {
    const database = this.database;
    if (database == null) return;
    const today = this.debug.getNowLocal().startOf('day');
    const computed = new Map<string, DayTaskStatuses>();
    // The token was created by the caller, synchronously, before any read. It
    // watches the memo epoch as well as the generation: `reactiveStatusGeneration`
    // only moves on a user-database switch, but the memo is ALSO invalidated by a
    // save during the sync, which evicts the affected days so the just-saved
    // entry is recomputed rather than served stale. That eviction bumps the
    // epoch and nothing else. Without watching it, a refresh whose read completed
    // before the save would write its pre-save statuses straight back over the
    // evicted days — and since a memo value now beats the cache mid-sync, that
    // resurrected value is what the calendar would render.
    //
    // counted for the summary line below: the display read is `include_docs`
    // over a whole cluster SPAN, so how many documents it actually pulls is the
    // one cost of this path that clustering does not bound (PRD step 2.6)
    let rawDocsRead = 0;
    // clusters whose read came back unavailable — those days are absent from
    // `computed`, so without this the line would report a small CLEAN tick and
    // hide that part of the work never happened
    let skippedClusters = 0;

    // One STRING-ONLY line per tick — never a second argument, and never inside
    // the loop. LoggingService replaces every console method, and anything other
    // than a lone string sends it down a heavy path: a deep serialize outside
    // prod, and a retained Sentry breadcrumb on every build. This function runs
    // in the catch-up sync's hot path, which is the exact path that rule was
    // written for (#4243).
    //
    // It reports on EVERY tick that did read work, including the abandoned ones.
    // Logging only on the success path would silently under-count both numbers
    // this line exists to produce — and it would under-count precisely in the
    // interesting cases (a save landing mid-sync, a user-database switch).
    const logTick = (outcome: string) =>
      console.log(`Reactive display-only refresh: ${computed.size}/${decision.dates.length} day(s) in `
        + `${decision.clusters.length} cluster(s), ${skippedClusters} skipped, `
        + `${rawDocsRead} raw doc(s) read, ${outcome}`);

    try {
      for (const cluster of decision.clusters) {
        const clusterStart = DateTime.fromISO(cluster.startDate);
        const clusterEnd = DateTime.fromISO(cluster.endDate);
        // The UTC-day buffer exists because a local day draws entries from the UTC
        // days either side of it, and it is wide enough to cover the full spread of
        // timezone offsets a reader can be in — see taskStatusIdRangeBounds, which
        // both raw-log reads in this file share so the width is decided once
        // (#5085). The end bound is taken from the END of the last day: an entry
        // stored with an offset BEHIND the device's current one (a DST change, or
        // travel) sits later in UTC than local midnight would suggest, and an end
        // bound taken from local midnight silently drops it.
        const { startUtc, endUtc } = taskStatusIdRangeBounds(clusterStart, clusterEnd);

        // a user-database switch, or a save that evicted memo days, can land during
        // this read. The read AND everything done with its result run inside
        // `run`, which checks only afterwards — the same point the loop used to
        // check at. An abandoned tick must still count the documents this cluster
        // read, or the line above under-counts exactly the ticks it exists to
        // describe.
        await flush.run(() => database.getByIdRangeLocalOnlyAsync('log/' + startUtc, 'log/' + endUtc + '\ufff0')
          .then(rawDocs => {
            // null means the read could not run at all (database recovering /
            // shutting down). That is not "no entries", so these days keep showing
            // whatever they already show. They are NOT retried by the next pulled
            // batch — this decision already consumed them — but they are still in the
            // persist list, so the post-sync flush recomputes them from complete data.
            if (rawDocs == null) {
              skippedClusters += 1;
              return;
            }
            rawDocsRead += rawDocs.length;
            this.collectDisplayClusterStatuses(rawDocs as Log_Entry[], cluster, decision, today, computed);
          }));
      }
    } catch (error) {
      // if a switch or an eviction landed while this ran, these statuses predate
      // it: writing them back would put the previous user's days, or exactly what
      // the eviction removed, into the memo. Report the tick with its reason. Any
      // other error is a real failure and travels up to the caller's report.
      if (!isReactiveFlushAbandoned(error)) throw error;
      logTick(`abandoned: ${error.reason}`);
      return;
    }

    if (computed.size === 0) { logTick('nothing computed'); return; }

    const changed = applyStatusesToMemoWithoutDowngrade(this.displayOnlyStatusMemo, computed);
    logTick(`changed=${changed}`);
    // publish only on a real change: an unchanged recompute would still reload
    // every open calendar for nothing
    if (changed) {
      this.pubSub.publish(TaskStatusCacheUpdatedEvent, {
        startDate: decision.rangeStart,
        endDate: decision.rangeEnd,
        clusters: decision.clusters,
      });
    }
  }

  /**
   * Work out one display cluster's day statuses from its raw log docs, and add
   * the days the decision asked about to `computed`. Synchronous on purpose:
   * `refreshDisplayOnlyStatusesAsync` runs it inside its token's `run`, before
   * the check, so it must not await anything.
   */
  private collectDisplayClusterStatuses(
    rawDocs: Log_Entry[],
    cluster: { startDate: string; endDate: string },
    decision: ReactiveDayStatusDecision,
    today: DateTime,
    computed: Map<string, DayTaskStatuses>,
  ): void {
    const clusterStart = DateTime.fromISO(cluster.startDate);
    const clusterEnd = DateTime.fromISO(cluster.endDate);
    const rows = logDocsToTaskStatusRows(rawDocs);
    const corruptTimeRows: TaskStatusQueryRow[] = [];
    const entriesByDate = groupTaskStatusRowsByDate(rows, clusterStart, clusterEnd, today, corruptTimeRows);
    this.reportCorruptTaskStatusRows(corruptTimeRows, TaskStatusReadPhase.Load);
    const statuses = buildDayStatusesForRange(entriesByDate, clusterStart, clusterEnd, today);

    // keep only the days this decision actually asked about; the cluster span
    // can be wider than the affected dates inside it
    for (const dateKey of decision.dates) {
      if (dateKey < cluster.startDate || dateKey > cluster.endDate) continue;
      const dayStatuses = statuses.get(dateKey);
      if (dayStatuses !== undefined) {
        computed.set(dateKey, dayStatuses);
      }
    }
  }

  /**
   * Flush the held reactive dates once the catch-up sync SUCCEEDS.
   *
   * "Succeeds", not "settles": the initial sync settles once per retry round, and
   * a round that gave up must not consume this waiter — while the sync counts as
   * incomplete nothing else will write these dates. So it subscribes to every
   * outcome and acts only on a successful one (#4435).
   *
   * Do NOT chase the dates by re-arming the timers instead — that is an unbounded
   * loop and invariant I5 forbids it. Why, plus the remaining bound:
   * `design-docs/sync-integrity.md`, the "SUCCEED, not to settle" paragraphs.
   */
  private armReactiveStatusPostSyncFlush(): void {
    if (this.reactiveStatusPostSyncFlushArmed) return;
    // the caller reaches this branch precisely when the sync is not complete,
    // which includes `this.database` being null — and with no database there is
    // nothing to subscribe to
    const database = this.database;
    if (database == null) return;
    this.reactiveStatusPostSyncFlushArmed = true;
    const generation = this.reactiveStatusGeneration;
    const stop = runOnInitialSyncSuccess(
      listener => database.onInitialSyncSettled(listener),
      () => {
        // a user-database switch happened while we waited: this outcome belongs
        // to the old database, and its held dates were cleared by the reset
        if (generation !== this.reactiveStatusGeneration) return;
        this.reactiveStatusPostSyncFlushArmed = false;
        this.reactiveStatusPostSyncFlushStop = null;
        void this.runReactiveStatusFlushAsync(true);
      },
    );
    // `onInitialSyncSettled` answers an already-settled sync on a microtask, so
    // today the callback above cannot have run yet. Check anyway rather than
    // assign blindly: if it ever did deliver synchronously, storing the handle
    // over the null the callback just wrote would leave a spent waiter looking
    // like a live one.
    if (this.reactiveStatusPostSyncFlushArmed) {
      this.reactiveStatusPostSyncFlushStop = stop;
    }
  }

  /**
   * The bounded fallback for a burst that touched too many days.
   *
   * A warm-resume sync delivers thousands of docs with the initial sync already
   * complete, so "post-initial-sync" is no guarantee of low volume. Recomputing
   * each affected day would mean dozens of reads and cache writes. Instead the
   * affected days are DELETED from the cache doc in one save and one event is
   * published; the normal load path then recomputes them properly (verified and
   * persisted) as each calendar asks for them.
   *
   * Deleting is the point. Simply publishing would make every page reload and
   * re-serve the very same stale cached values.
   *
   * This is the one acting path that does NOT read `decision.clusters` — it does
   * no per-day recompute. It only forwards them onto the published event, so a
   * page can tell whether any changed day is on screen (#4451).
   */
  private async invalidateTaskStatusDatesAsync(flush: ReactiveFlushToken, decision: ReactiveDayStatusDecision): Promise<void> {
    const { dates, rangeStart, rangeEnd } = decision;
    if (dates.length === 0 || rangeStart == null || rangeEnd == null) return;

    // the mutex wait can span a user-database switch; these dates describe the
    // previous database, so dropping them from this one's cache would delete
    // another user's statuses. The token checks once the lock is held.
    await flush.exclusiveCall(this.taskStatusCacheMutex, async () => {
      // the load is an await too, and it returns the DATABASE'S identity-mapped
      // singleton — so after a switch `cacheDoc` is the NEW user's doc. `run`
      // checks BEFORE handing it back, so the delete loop below never runs on it:
      // mutating it and then declining to save would still leave the object
      // stripped, and the next writer to save that singleton would persist the
      // new user's cache with those days missing.
      const cacheDoc = await flush.run(() => this.loadDayTaskStatusCacheAsync());
      const cache = new DayTaskStatusCacheDoc(cacheDoc);
      let removedAny = false;
      for (const dateKey of dates) {
        if (cache.deleteDay(dateKey)) {
          removedAny = true;
        }
      }
      if (removedAny) {
        await flush.run(() => this.saveDayTaskStatusCacheAsync(DatabaseWrapper.getTransactionId()));
      }
    });

    // reaching here means the token found no switch after the lock was released
    // either: publishing the OLD user's date range would make the NEW user's
    // calendar reload a range that has nothing to do with them

    console.log(`Reactive task status invalidated ${dates.length} day(s) in ${rangeStart}..${rangeEnd} (bounded fallback)`);
    // publish DIRECTLY, never through armPostSyncTaskStatusRefresh: in steady
    // state the sync has already settled, so that one-shot would fire straight
    // away and drive the reload cycle its own comment warns about
    this.pubSub.publish(TaskStatusCacheUpdatedEvent,
      { startDate: rangeStart, endDate: rangeEnd, clusters: decision.clusters });
  }

  /**
   * Recompute and persist the days a pulled change affected.
   *
   * This is the reactive twin of updateTaskStatusCacheForEntriesAsync, with one
   * deliberate difference: it does NOT supersede the read with entries held in
   * hand. That step exists to protect a LOCAL save from a view that has not
   * indexed it yet. A pulled doc is already in local PouchDB, so the verified
   * raw-doc read is the sole source of truth for it (I3) — and passing dates
   * rather than doc bodies is what makes a stale revision in a sync burst
   * harmless by construction.
   *
   * Runs under the cache mutex, like every other cache writer: an unmutexed
   * writer could interleave with a page load or a local save and lose one
   * side's day writes.
   */
  private async recomputeAndPersistDatesAsync(flush: ReactiveFlushToken, decision: ReactiveDayStatusDecision): Promise<void> {
    const today = this.debug.getNowLocal().startOf('day');
    const affectedDates = new Set(decision.dates);
    const unpersistedDates: string[] = [];
    let persistedAny = false;

    // the mutex wait can span a user-database switch — see the same call in
    // invalidateTaskStatusDatesAsync
    await flush.exclusiveCall(this.taskStatusCacheMutex, async () => {
      // same reasoning as invalidateTaskStatusDatesAsync: the load is an await,
      // and it hands back the database's identity-mapped singleton, so a switch
      // across it means every write below would land on the new user's doc
      const cacheDoc = await flush.run(() => this.loadDayTaskStatusCacheAsync());
      // the owner is the only way to store a day, and it also makes sure the
      // document has a day map (df-827a7f3bf41e)
      const cache = new DayTaskStatusCacheDoc(cacheDoc);

      for (const cluster of decision.clusters) {
        const clusterStart = DateTime.fromISO(cluster.startDate);
        const clusterEnd = DateTime.fromISO(cluster.endDate);
        const clusterDates = decision.dates.filter(d => d >= cluster.startDate && d <= cluster.endDate);

        // a switch can also land during each cluster's read, including the LAST
        // one, whose result would otherwise reach the save below. The work on the
        // result runs inside `run` too, before its check — the same point the
        // loop used to check at — so a read that crossed a switch still reports
        // any corrupt entry times it found. Nothing it stores is ever saved.
        await flush.run(() => this.readTaskStatusRowsAsync(clusterStart, clusterEnd, today, TaskStatusReadPhase.Load)
          .then(read => {
            // an unavailable read (offline, database recovering) or one that could
            // not be cross-checked against the raw docs must never be persisted
            // (#4212 / #4219). Requeue instead of writing a wrong "no task".
            if (read == null || !read.verified) {
              unpersistedDates.push(...clusterDates);
              return;
            }
            if (this.storeClusterStatuses(cache, read.rows, cluster, clusterDates, today)) {
              persistedAny = true;
            }
          }));
      }

      if (persistedAny) {
        await flush.run(() => this.saveDayTaskStatusCacheAsync(DatabaseWrapper.getTransactionId()));
      }
    });

    // reaching here means no user-database switch happened mid-flush. Had one
    // happened, these dates would belong to the old database and were already
    // cleared by the reset, so requeueing or publishing below would act on the
    // wrong user's calendar — the token threw instead, and nothing below runs.

    // dates whose read could not be trusted go back into the pending set and are
    // retried; they are never silently dropped (I6)
    if (unpersistedDates.length > 0) {
      this.reactiveDayStatusCoalescer.requeue(unpersistedDates, Date.now(), decision.action);
      this.reactiveStatusFlushRequeued = true;
      // report once per failure streak, not once per retry: a wedged read that
      // keeps answering unverified would otherwise flood telemetry
      if (this.reactiveStatusConsecutiveFailures === 0) {
        // INFO tier, not problem: this is expected noise, not a defect. The read
        // came back unverifiable — usually the eventually-consistent view index
        // lagging a just-written doc — so the flush refuses to persist and
        // requeues, and it self-heals on the retry. #4382 moved this canary's two
        // siblings to the info tier for exactly that reason and DELETED their E2E
        // allow-list entries; leaving this one on the `problem` tier would fail
        // that gate with no entry left to cover it. See design-docs/telemetry.md,
        // "Two traps when changing a report's tier".
        this.logging.trackSilentInfo('TaskStatusReactiveFlushUnverified', {
          dateCount: unpersistedDates.length,
          totalDates: affectedDates.size,
          start: decision.rangeStart,
          end: decision.rangeEnd,
        });
      }
    }

    // one string-only line per flush — never per document. This is the number
    // to watch if the reactive path is ever suspected of churning: one flush per
    // sync batch is expected, one flush per arriving doc is a bug.
    console.log(`Reactive task status flush: ${decision.dates.length} day(s) in ${decision.clusters.length} cluster(s), `
      + `persisted=${persistedAny}, requeued=${unpersistedDates.length}`);

    // one event per flush window, covering the whole affected span — with the
    // clusters, so a page whose visible days hold none of them can skip its
    // reload (#4451). These 14-or-fewer days can still be scattered across
    // years, so the span alone can straddle a page's visible days with none of
    // the changed ones inside.
    if (persistedAny && decision.rangeStart != null && decision.rangeEnd != null) {
      this.pubSub.publish(TaskStatusCacheUpdatedEvent, {
        startDate: decision.rangeStart,
        endDate: decision.rangeEnd,
        clusters: decision.clusters,
      });
    }
  }

  /**
   * Work out one persisting cluster's day statuses from verified rows and store
   * the days the decision asked about in the cache document. Returns whether it
   * stored any. Synchronous on purpose: `recomputeAndPersistDatesAsync` runs it
   * inside its token's `run`, before the check, so it must not await anything.
   */
  private storeClusterStatuses(
    cache: DayTaskStatusCacheDoc,
    rows: TaskStatusQueryRow[],
    cluster: { startDate: string; endDate: string },
    clusterDates: string[],
    today: DateTime,
  ): boolean {
    const clusterStart = DateTime.fromISO(cluster.startDate);
    const clusterEnd = DateTime.fromISO(cluster.endDate);
    const corruptTimeRows: TaskStatusQueryRow[] = [];
    const entriesByDate = groupTaskStatusRowsByDate(rows, clusterStart, clusterEnd, today, corruptTimeRows);
    this.reportCorruptTaskStatusRows(corruptTimeRows, TaskStatusReadPhase.Load);
    const statuses = buildDayStatusesForRange(entriesByDate, clusterStart, clusterEnd, today);

    let storedAny = false;
    for (const dateKey of clusterDates) {
      const dayStatuses = statuses.get(dateKey);
      if (dayStatuses !== undefined) {
        cache.putDay(dateKey, dayStatuses, today);
        storedAny = true;
      }
    }
    return storedAny;
  }

  /**
   * Arm a one-shot: when the initial (catch-up) sync SUCCEEDS, publish
   * TaskStatusCacheUpdatedEvent so open calendars reload — their reload then
   * recomputes from complete local data and persists (#4219).
   *
   * "Succeeds", not "settles". The initial sync settles once per retry round, and
   * a round that gave up is not an answer to the question this waiter is asking —
   * while the sync counts as incomplete, nothing persists these days. So it
   * subscribes to every outcome and acts only on a successful one, the same way
   * armReactiveStatusPostSyncFlush above does (#4435).
   *
   * It used to await `database.whenInitialSyncCompleted` once instead. That
   * promise settles once and the retry loop replaces it per round, so a waiter
   * woken by a failed round was left holding a promise that would never speak
   * again, and the round that finally succeeded told it nothing — the calendar
   * then kept its display-only values until the user opened a calendar page
   * again and re-armed this. The trap is written up once, for every site that
   * reaches for that promise, in `design-docs/sync-integrity.md`.
   */
  private armPostSyncTaskStatusRefresh(startDate: DateTime, endDate: DateTime) {
    // the caller reaches this branch precisely when the sync is not complete,
    // which includes `this.database` being null — and with no database there is
    // nothing to subscribe to
    const database = this.database;
    if (database == null) return;

    // widen the armed span rather than dropping this call's dates. Subscribers
    // skip a reload whose span misses the days they are showing, and this waiter
    // now stays armed across failed rounds — so a span captured by the first
    // arming would leave a page opened during the wait unreloaded.
    this.postSyncTaskStatusRefreshRange = widenTaskStatusRange(
      this.postSyncTaskStatusRefreshRange, startDate.toISODate(), endDate.toISODate());

    if (this.postSyncTaskStatusRefreshArmed) return;
    this.postSyncTaskStatusRefreshArmed = true;
    const generation = this.reactiveStatusGeneration;
    const stop = runOnInitialSyncSuccess(
      listener => database.onInitialSyncSettled(listener),
      () => {
        // a user-database switch happened while we waited: this outcome belongs
        // to the old database, and the span describes the old user's calendar.
        // Return before touching any field — the reset cleared them all, and a
        // fresh waiter may already be armed in this new generation.
        if (generation !== this.reactiveStatusGeneration) return;
        this.postSyncTaskStatusRefreshArmed = false;
        this.postSyncTaskStatusRefreshStop = null;
        const eventRange = this.postSyncTaskStatusRefreshRange;
        this.postSyncTaskStatusRefreshRange = null;
        if (eventRange == null) return;
        this.pubSub.publish(TaskStatusCacheUpdatedEvent, eventRange);
      },
    );
    // `onInitialSyncSettled` answers an already-settled sync on a microtask, so
    // today the callback above cannot have run yet. Check anyway rather than
    // assign blindly: if it ever did deliver synchronously, storing the handle
    // over the null the callback just wrote would leave a spent waiter looking
    // like a live one.
    if (this.postSyncTaskStatusRefreshArmed) {
      this.postSyncTaskStatusRefreshStop = stop;
    }
  }

  /**
   * Combine individual task statuses into a single status for calendar display.
   * Priority: Incomplete > InProgress > Completed > NoTask > Future
   */
  combineDayTaskStatuses(statuses: DayTaskStatuses): TaskStatus {
    const allStatuses = [
      statuses.workoutStatus,
      statuses.mealsStatus,
      statuses.weighInStatus,
      statuses.weeklyCheckupStatus
    ];

    // if all tasks are future, the day is future
    if (allStatuses.every(s => s === TaskStatus.Future)) {
      return TaskStatus.Future;
    }

    // if any task is in progress, the day is in progress
    if (allStatuses.some(s => s === TaskStatus.InProgress)) {
      return TaskStatus.InProgress;
    }

    // if any task is incomplete, the day is incomplete
    if (allStatuses.some(s => s === TaskStatus.Incomplete)) {
      return TaskStatus.Incomplete;
    }

    // if any task is completed, the day is completed
    if (allStatuses.some(s => s === TaskStatus.Completed)) {
      return TaskStatus.Completed;
    }

    // otherwise, no tasks for the day
    return TaskStatus.NoTask;
  }

  /**
   * Read the task-status view rows for [startDate, endDate], with the safety
   * layers from #4212 and #4219:
   * - returns `null` when the query could not run at all (db recovering /
   *   shutting down) — the caller must NOT persist anything in that case;
   * - after the catch-up sync, EVERY read over past days is verified against a
   *   view-independent allDocs id-range read (log doc ids embed the UTC
   *   timestamp), and the raw docs win on any discrepancy — a wedged view
   *   index can lie PARTIALLY, not just with zero rows (#4219). A confirmed
   *   lie is reported and a one-time index rebuild is requested;
   * - `verified: false` marks rows that could not be checked (catch-up sync
   *   still running, or the raw read unavailable) — display them, never
   *   persist them.
   *
   * #4249 Phase 3 made the view query SAMPLED on the post-sync path. The raw
   * docs already decide what the user sees there, so most post-sync reads skip
   * the view entirely; one read in `TASK_STATUS_VIEW_SAMPLE_RATE` still runs it
   * and compares, which is what keeps the #4343 regression detector alive. The
   * other two paths are NOT sampled and behave exactly as before: a future-only
   * range has no raw-doc source at all (the view is its only data), and a
   * mid-catch-up read must keep its remote-capable rescue.
   */
  private async readTaskStatusRowsAsync(
    startDate: DateTime,
    endDate: DateTime,
    today: DateTime,
    phase: TaskStatusReadPhase,
  ): Promise<TaskStatusRowsRead | null> {
    // Query with a buffer to account for timezone differences: a local day draws
    // entries from the UTC days either side of it, and the buffer is wide enough
    // to cover every timezone offset a reader can be in (#5085). Both the view
    // query and the raw id-range read below use these same two bounds, which is
    // what keeps the #4219 cross-check comparing the same population on both
    // sides. See taskStatusIdRangeBounds for how wide it is and why.
    const { startUtc, endUtc } = taskStatusIdRangeBounds(startDate, endDate);

    // Run the view query. Returns null when the query could not run at all —
    // that is "unavailable", NOT "no entries", and every caller of this helper
    // has to carry that distinction. Persisting a zero produced by a shut-down
    // database is exactly the #4212 cache poisoning.
    //
    // The wrapper signals unavailable by THROWING (it used to return null), so
    // that decision lives in a catch. Returning null from this closure keeps the
    // callers' existing "unknown, do not persist" signal unchanged.
    const runViewQueryAsync = async (viewCheck: TaskStatusViewCheck): Promise<TaskStatusQueryRow[] | null> => {
      try {
        return await this.database.runQueryAsync('logEntriesForTaskStatus', undefined, {
          startkey: startUtc,
          endkey: endUtc,
          include_docs: false  // We don't need full docs, just the emitted values
        }) as TaskStatusQueryRow[];
      } catch (err) {
        if (!isDatabaseUnavailable(err)) throw err;
        // our own sign-out wipe is not a fault, and `trackSilentProblem` writes a
        // console.error the E2E gate fails on (no allow-list entry for this
        // name). The `null` return below is unchanged either way, so the #4212
        // do-not-persist signal still reaches the caller.
        if (isDatabaseTornDown(err)) {
          console.log(`Task-status view query skipped — our own teardown (${err.reason})`);
          return null;
        }
        this.reportUnavailableTaskStatusRead(err, {
          phase, start: startDate.toISODate(), end: endDate.toISODate(), reason: err.reason,
          // this site fires from all three branches, and only the post-sync one
          // is sampled — so `viewCheck` is what tells a reader whether this
          // particular count needs scaling by `sampleRate` (#4249 Phase 3)
          viewCheck, sampleRate: this.taskStatusViewSampler.rate,
        });
        return null;
      }
    };

    // read the raw log docs for the range - the view-independent ground truth.
    // `null` means "could not read", which both consumers below already treat as
    // unavailable rather than as "no docs" (#4212 / #4219). getByIdRangeAsync now
    // throws for that instead of returning null, so the catch here keeps this
    // closure's existing contract and leaves that downstream logic untouched.
    // the raw read reports "could not read" as `null`, which loses WHY. The
    // reporting below needs that: our own teardown must stay quiet, a recovery
    // that cut the read short is only worth an info record (#6209), anything
    // else is a problem. Carry the refusal out in this variable rather than
    // widening the closure's return type, which every caller would then have to
    // unpack.
    let rawReadRefusal: DatabaseUnavailableError | null = null;
    const readRawLogDocsAsync = async (): Promise<Log_Entry[] | null> => {
      return this.database.withDocWatcherAsync(async watcher => {
        try {
          return await this.database.getByIdRangeAsync('log/' + startUtc, 'log/' + endUtc + '\ufff0', watcher);
        } catch (err) {
          if (!isDatabaseUnavailable(err)) throw err;
          rawReadRefusal = err;
          return null;
        }
      });
    };
    const lastRawReadRefusal = () => rawReadRefusal;

    // a future-only range has nothing the view could lie about yet — and no raw
    // docs either, so the view is its ONLY data source. never sampled.
    const includesPastDays = startDate.startOf('day') <= today;
    if (!includesPastDays) {
      const rows = await runViewQueryAsync(TaskStatusViewCheck.Always);
      if (rows == null) return null;
      return { rows, verified: true, rowsCameFrom: TaskStatusRowSource.View };
    }

    // mid-catch-up reads get only the #4212 zero-row rescue and stay
    // unverified. never sampled: skipping the query here would leave the rescue
    // looking at zero rows on EVERY read, so it would report a lying view every
    // time — the exact canary storm sampling exists to prevent.
    const initialSyncCompleted = this.database.initialSyncCompletedState === true;
    if (!initialSyncCompleted) {
      const rows = await runViewQueryAsync(TaskStatusViewCheck.Always);
      if (rows == null) return null;
      return this.rescueRowsDuringCatchupAsync(rows, readRawLogDocsAsync, lastRawReadRefusal, startDate, endDate, phase);
    }

    // post-sync: the raw docs serve the dots either way (#4219), so on an
    // unsampled read we simply never ask the view (#4249 Phase 3)
    if (!this.taskStatusViewSampler.shouldSampleThisRead()) {
      return this.serveRowsFromRawDocsAsync(readRawLogDocsAsync, lastRawReadRefusal, startDate, endDate, phase);
    }
    const rows = await runViewQueryAsync(TaskStatusViewCheck.Sampled);
    if (rows == null) {
      // the sample was spent but no comparison happened, so put it back — see
      // TaskStatusViewSampler.rearm for why a database that keeps going
      // unavailable would otherwise be checked far less often than N suggests
      this.taskStatusViewSampler.rearm();
      return null;
    }
    return this.verifyRowsAgainstRawDocsAsync(rows, readRawLogDocsAsync, lastRawReadRefusal, startDate, endDate, phase);
  }

  /**
   * Mid-catch-up branch of readTaskStatusRowsAsync: local data may be
   * legitimately incomplete, so a full verification is meaningless. Keep only
   * the #4212 zero-row rescue (its read may pass through to the remote, which
   * is exactly what a mid-sync display needs) and mark the result unverified \u2014
   * the caller displays it but never persists it (#4219).
   */
  private async rescueRowsDuringCatchupAsync(
    rows: TaskStatusQueryRow[],
    readRawLogDocsAsync: () => Promise<Log_Entry[] | null>,
    lastRawReadRefusal: () => DatabaseUnavailableError | null,
    startDate: DateTime,
    endDate: DateTime,
    phase: TaskStatusReadPhase,
  ): Promise<TaskStatusRowsRead | null> {
    // if the view returned anything, serve it as-is (unverified)
    if (rows.length > 0) {
      return { rows, verified: false, rowsCameFrom: TaskStatusRowSource.View };
    }

    const logDocs = await readRawLogDocsAsync();
    if (logDocs == null) {
      // the cross-check read could not run either - the zero-row view result
      // is unverifiable, so treat the whole read as unavailable (never persist
      // no_task from an unverified empty result, #4212)
      // our own teardown is not a fault, and this canary writes a console.error
      // the E2E gate fails on. Narrow window — the shutdown has to land BETWEEN
      // the view query and this read — but it is the same rule as every other
      // site, and the `null` return below is unchanged either way (#4212).
      const refusal = lastRawReadRefusal();
      if (isDatabaseTornDown(refusal)) {
        console.log('Task-status catch-up rescue skipped — database torn down');
        return null;
      }
      this.reportUnavailableTaskStatusRead(refusal, {
        phase, source: 'alldocs-fallback', start: startDate.toISODate(), end: endDate.toISODate(),
        // this branch always queries, so its count is unchanged by Phase 3 and
        // must NOT be scaled — `viewCheck` is what says so. `sampleRate` rides
        // along on every cross-check fire site regardless, so a reader always
        // knows what N was at the time, whether or not this row may be scaled
        viewCheck: TaskStatusViewCheck.Always, sampleRate: this.taskStatusViewSampler.rate,
      });
      return null;
    }
    const zeroRowFallback = logDocsToTaskStatusRows(logDocs);
    if (zeroRowFallback.length > 0) {
      this.logging.trackSilentInfo('TaskStatusViewMissedLogDocs', {
        phase, start: startDate.toISODate(), end: endDate.toISODate(), allDocsCount: zeroRowFallback.length,
        // the mid-catch-up branch is never sampled, so this fire site's rate is
        // unchanged by Phase 3 and must NOT be scaled by the sample rate.
        // same payload shape as its sibling above, on purpose
        viewCheck: TaskStatusViewCheck.Always, sampleRate: this.taskStatusViewSampler.rate,
      });
      return { rows: zeroRowFallback, verified: false, rowsCameFrom: TaskStatusRowSource.RawDocs };
    }
    return { rows, verified: false, rowsCameFrom: TaskStatusRowSource.View };
  }

  /**
   * Post-catch-up branch of readTaskStatusRowsAsync: verify EVERY past-range
   * read against the raw docs. #4212 only rescued the zero-row case; #4219
   * showed a wedged index also lies with a partial answer, a stale state, or
   * a ghost row. The raw docs always win; a confirmed lie is reported and an
   * index rebuild is requested.
   */
  private async verifyRowsAgainstRawDocsAsync(
    rows: TaskStatusQueryRow[],
    readRawLogDocsAsync: () => Promise<Log_Entry[] | null>,
    lastRawReadRefusal: () => DatabaseUnavailableError | null,
    startDate: DateTime,
    endDate: DateTime,
    phase: TaskStatusReadPhase,
  ): Promise<TaskStatusRowsRead | null> {
    const rawRead = await this.readRawDocsOrHandleUnavailableAsync(
      readRawLogDocsAsync, lastRawReadRefusal, rows, startDate, endDate, phase, TaskStatusViewCheck.Sampled,
    );
    if (rawRead.status === RawDocsReadStatus.Unavailable) {
      // this read held a sample but never got to compare anything
      this.taskStatusViewSampler.rearm();
      return rawRead.result;
    }

    const fallbackRows = logDocsToTaskStatusRows(rawRead.logDocs);
    const reconciled = reconcileViewRowsWithLogDocs(rows, fallbackRows);
    if (reconciled.viewDiscrepancy) {
      // the local view index is provably lying - report it (with enough shape
      // to tell a partial lie from the #4212 zero-row lie) and request a
      // one-time rebuild of that index on this device (#4219)
      this.logging.trackSilentInfo('TaskStatusViewMissedLogDocs', {
        phase,
        partial: rows.length > 0,
        viewRowCount: rows.length,
        allDocsCount: fallbackRows.length,
        start: startDate.toISODate(),
        end: endDate.toISODate(),
        // this fire site now only sees one read in `sampleRate`, so a future
        // reader must multiply by it to recover the real rate (#4249 Phase 3)
        viewCheck: TaskStatusViewCheck.Sampled,
        sampleRate: this.taskStatusViewSampler.rate,
      });
      this.requestTaskStatusViewHeal();
    }
    return { rows: reconciled.rows, verified: true, rowsCameFrom: TaskStatusRowSource.RawDocsCheckedAgainstView };
  }

  /**
   * The UNSAMPLED post-sync branch (#4249 Phase 3): no view query ran at all,
   * so build the rows straight from the raw log docs and serve them.
   *
   * This is not a downgrade in what the user sees. Since #4219 the raw docs are
   * what the status math uses on every post-sync read anyway; the view's answer
   * was only ever compared against them to catch a lying index. Skipping the
   * comparison means we do not detect a lie on THIS read — the sampled reads
   * are what detect it — so the rows are still `verified: true` and safe to
   * persist.
   *
   * It deliberately does NOT call `verifyRowsAgainstRawDocsAsync` with an empty
   * row list. That method's reconcile treats "no view rows" as proof the view
   * lied, so it would report `TaskStatusViewMissedLogDocs` and request a heal on
   * essentially every unsampled read — a canary storm, and exactly the opposite
   * of what sampling is for.
   */
  private async serveRowsFromRawDocsAsync(
    readRawLogDocsAsync: () => Promise<Log_Entry[] | null>,
    lastRawReadRefusal: () => DatabaseUnavailableError | null,
    startDate: DateTime,
    endDate: DateTime,
    phase: TaskStatusReadPhase,
  ): Promise<TaskStatusRowsRead | null> {
    // no view answer exists on this path, so pass an empty list: the shared
    // handler's "nothing to display" arm is the right one, and it returns the
    // #4212 do-not-persist null
    const rawRead = await this.readRawDocsOrHandleUnavailableAsync(
      readRawLogDocsAsync, lastRawReadRefusal, [], startDate, endDate, phase, TaskStatusViewCheck.Skipped,
    );
    if (rawRead.status === RawDocsReadStatus.Unavailable) {
      return rawRead.result;
    }
    return { rows: logDocsToTaskStatusRows(rawRead.logDocs), verified: true, rowsCameFrom: TaskStatusRowSource.RawDocs };
  }

  /**
   * Do the view-independent raw docs read, and handle the case where it could
   * not run at all. Shared by the sampled and unsampled post-sync branches so
   * the #4212 do-not-persist signal behaves identically on both.
   *
   * Which arm applies is the pure `decideUnavailableRawReadOutcome`, and both
   * arms matter:
   * - with NO view rows to fall back on, the answer is `null` — "unavailable",
   *   which tells the caller not to persist. Persisting `no_task` from an
   *   unverifiable empty answer is exactly the #4212 cache poisoning.
   * - with view rows in hand (only possible on a sampled read), they are still
   *   worth showing, so they are returned `verified: false` — display, never
   *   persist.
   *
   * `viewCheck` says how this read reached the raw-docs read, and does two
   * jobs: it rides into the telemetry so a count can be scaled correctly, and it
   * picks the `source` label. That label matters — a read that never queried the
   * view must not be reported under a name that sends a debugger looking at the
   * view's answer.
   *
   * `viewRowsToDisplay` is empty by construction on the unsampled path (no query
   * ran), which is why that path always lands on the do-not-persist arm.
   */
  private async readRawDocsOrHandleUnavailableAsync(
    readRawLogDocsAsync: () => Promise<Log_Entry[] | null>,
    lastRawReadRefusal: () => DatabaseUnavailableError | null,
    viewRowsToDisplay: TaskStatusQueryRow[],
    startDate: DateTime,
    endDate: DateTime,
    phase: TaskStatusReadPhase,
    viewCheck: TaskStatusViewCheck,
  ): Promise<RawTaskStatusDocsReadOutcome> {
    const logDocs = await readRawLogDocsAsync();
    if (logDocs != null) {
      return { status: RawDocsReadStatus.Available, logDocs };
    }
    // the raw read only returns null for an unavailable database, so during our
    // own sign-out wipe both arms below would report a problem for something
    // that is not one — and write a console.error the E2E gate fails on. The
    // RESULT is unchanged; only the reporting is skipped.
    const refusal = lastRawReadRefusal();
    if (isDatabaseTornDown(refusal)) {
      console.log('Task-status raw read skipped — our own teardown');
      return decideUnavailableRawReadOutcome(viewRowsToDisplay.length) === UnavailableRawReadOutcome.DoNotPersist
        ? { status: RawDocsReadStatus.Unavailable, result: null }
        : { status: RawDocsReadStatus.Unavailable, result: { rows: viewRowsToDisplay, verified: false, rowsCameFrom: TaskStatusRowSource.View } };
    }

    const context = {
      phase,
      start: startDate.toISODate(),
      end: endDate.toISODate(),
      viewCheck,
      sampleRate: this.taskStatusViewSampler.rate,
    };
    if (decideUnavailableRawReadOutcome(viewRowsToDisplay.length) === UnavailableRawReadOutcome.DoNotPersist) {
      this.reportUnavailableTaskStatusRead(refusal, {
        ...context,
        // name the two situations apart: 'alldocs-fallback' means the view
        // answered zero rows and the raw read then failed, so the empty answer
        // is unverifiable; 'alldocs-unsampled' means no view query ran at all
        source: viewCheck === TaskStatusViewCheck.Skipped ? 'alldocs-unsampled' : 'alldocs-fallback',
      });
      return { status: RawDocsReadStatus.Unavailable, result: null };
    }
    this.reportUnavailableTaskStatusRead(refusal, {
      ...context, source: 'alldocs-verify',
    });
    return { status: RawDocsReadStatus.Unavailable, result: { rows: viewRowsToDisplay, verified: false, rowsCameFrom: TaskStatusRowSource.View } };
  }

  /**
   * Report a task-status read the database refused, after the caller has
   * already handled our own teardown. Reporting only: every caller returns the
   * same "unknown, do not persist" answer whatever happens here (#4212 / #4219).
   *
   * A refusal because a database recovery cut the read short (#6209) heals
   * itself: the dates are requeued or recomputed, so the only loss is a
   * calendar dot that arrives a little later. That is the info tier, under its
   * own name. Every other reason is still unresolved and stays a problem.
   * `refusal` is null only when nothing recorded why, which is reported as a
   * problem rather than guessed at.
   */
  private reportUnavailableTaskStatusRead(refusal: DatabaseUnavailableError | null, context: Record<string, unknown>): void {
    if (isDatabaseRecovering(refusal)) {
      this.logging.trackSilentInfo('TaskStatusQueryDeferred', { ...context, reason: refusal.reason });
      return;
    }
    this.logging.trackSilentProblem('TaskStatusQueryUnavailable', context);
  }

  /**
   * Ask the database wrapper to rebuild the task-status view index from
   * scratch (fire-and-forget; rate-limited inside the wrapper). Reported so
   * the heal itself is visible in telemetry (#4219).
   *
   * Only the SAMPLED cross-check calls this, so its rate fell in #4249 Phase 3.
   * The `sampleRate` field records by how much we now look, but do NOT simply
   * multiply this count by it: the heal is rate-limited per device inside
   * `requestViewHealAsync` and only reports when one was actually scheduled, so
   * the count does not scale linearly and multiplying would overstate it. For
   * how many devices are affected, read the distinct-user count instead.
   */
  private requestTaskStatusViewHeal() {
    void this.database.requestViewHealAsync('logEntriesForTaskStatus').then((scheduled) => {
      if (scheduled) {
        this.logging.trackSilentInfo('TaskStatusViewHealScheduled', {
          view: 'logEntriesForTaskStatus',
          viewCheck: TaskStatusViewCheck.Sampled,
          sampleRate: this.taskStatusViewSampler.rate,
        });
      }
    });
  }

  /**
   * Calculate task statuses for specific dates using the optimized query.
   * Much faster than loading all log entries and filtering in memory.
   * Returns individual statuses for each category, or `null` when the query
   * could not run (the caller must not persist anything then — #4212).
   * `verified: false` marks statuses computed from data that could not be
   * cross-checked (catch-up sync running / raw read unavailable) — the caller
   * may display them but must not persist them (#4219).
   */
  private async calculateTaskStatusesForDatesAsync(startDate: DateTime, endDate: DateTime): Promise<{ statuses: Map<string, DayTaskStatuses>; verified: boolean } | null> {
    const today = this.debug.getNowLocal().startOf('day');

    const read = await this.readTaskStatusRowsAsync(startDate, endDate, today, TaskStatusReadPhase.Load);
    if (read == null) {
      return null;
    }

    const corruptTimeRows: TaskStatusQueryRow[] = [];
    const entriesByDate = groupTaskStatusRowsByDate(read.rows, startDate, endDate, today, corruptTimeRows);
    this.reportCorruptTaskStatusRows(corruptTimeRows, TaskStatusReadPhase.Load);
    return { statuses: buildDayStatusesForRange(entriesByDate, startDate, endDate, today), verified: read.verified };
  }

  /**
   * Surface rows the status calculation had to skip because their stored
   * entry time cannot be parsed — a data-integrity condition that must reach
   * a future debugger instead of being silently dropped (#4212).
   */
  private reportCorruptTaskStatusRows(corruptTimeRows: TaskStatusQueryRow[], phase: TaskStatusReadPhase) {
    if (corruptTimeRows.length === 0) return;
    this.logging.trackSilentProblem('TaskStatusCorruptEntryTime', {
      phase,
      count: corruptTimeRows.length,
      sampleKey: corruptTimeRows[0].key,
    });
  }

  /**
   * Update cache for affected dates when log entries change.
   * Uses the optimized query approach for better performance.
   */
  async updateTaskStatusCacheForEntriesAsync(entries: Log_Entry[]): Promise<void> {
    if (!entries || entries.length === 0) return;

    const today = this.debug.getNowLocal().startOf('day');

    // Group entries by date
    const affectedDates = new Set<string>();

    for (const entry of entries) {
      // Only process entry types that affect task status
      if (entry.type === LogRecordType.Workout ||
        entry.type === LogRecordType.Meal ||
        entry.type === LogRecordType.WeighIn ||
        entry.type === LogRecordType.WeeklyCheckup) {
        const entryDate = getEntryLocalTimeInDesiredTimeZone(entry, today);
        affectedDates.add(entryDate.toISODate());
      }
    }

    // If no relevant entries, nothing to update
    if (affectedDates.size === 0) return;

    await this.taskStatusCacheMutex.exclusiveCall(async () => {
      try {
        // Find the date range to query
        const dateArray = Array.from(affectedDates).map(d => DateTime.fromISO(d));
        const minDate = DateTime.min(...dateArray);
        const maxDate = DateTime.max(...dateArray);

        // while the catch-up sync is running, the view can be missing the
        // day's OTHER entries (not yet pulled), so a recompute now could
        // persist no_task for them (#4219). skip the write, and hand the days
        // to the reactive path, which holds them until the sync succeeds and
        // then recomputes and persists them (#6258).
        if (this.database.initialSyncCompletedState !== true) {
          console.log(`Task status update skipped during catch-up sync for ${minDate.toISODate()}..${maxDate.toISODate()}`);
          // drop the affected days from the display-only memo: the reload we
          // trigger below must recompute them, so the just-saved entry shows
          // up instead of the memoized pre-save statuses. The epoch bump makes
          // an in-flight display refresh (whose read may predate this save)
          // throw its results away instead of resurrecting the evicted days.
          for (const dateKey of affectedDates) {
            this.displayOnlyStatusMemo.delete(dateKey);
          }
          this.displayOnlyMemoEpoch++;
          // the one-shot below only reloads calendars, and the load path serves
          // a stored day whose value is possible for its date, so hand the days
          // to the coalescer, which persists them after the sync (#6258, see
          // design-docs/sync-integrity.md)
          this.scheduleTaskStatusRecompute(affectedDates);
          // the reload the publish below triggers redraws the evicted days from
          // the just-saved data; the one-shot reloads open calendars again once
          // the sync completes (the coalescer's flush publishes its own reload
          // after it has persisted the days)
          this.armPostSyncTaskStatusRefresh(minDate, maxDate);
          // still notify open calendars so the just-saved entry shows up via
          // the (display-only) load path
          this.pubSub.publish(TaskStatusCacheUpdatedEvent, {
            startDate: minDate.toISODate(),
            endDate: maxDate.toISODate()
          });
          return;
        }

        // Load the cache singleton
        const cacheDoc = await this.loadDayTaskStatusCacheAsync();
        // the owner is the only way to store a day, and it also makes sure the
        // document has a day map (df-827a7f3bf41e)
        const cache = new DayTaskStatusCacheDoc(cacheDoc);

        const read = await this.readTaskStatusRowsAsync(minDate, maxDate, today, TaskStatusReadPhase.Update);
        // Neither branch below may persist, and neither may simply return either.
        // They used to, on the stated grounds that "the next load recomputes" —
        // which is false: the load path serves any day already in the cache
        // document whose value is possible for its date, so the day keeps its
        // pre-save value and the user's just-finished task shows the wrong dot
        // for the rest of the day (#4694). Handing the days to the reactive path
        // retries them once a read can be trusted, and publishes.
        //
        // Deleting the days instead looks obvious and is wrong; the reasoning is
        // in `design-docs/sync-integrity.md`, under the #4694 bullet.
        if (read == null) {
          // the query could not run — persisting now would write wrong "no task"
          // values into the cache (#4212)
          this.scheduleTaskStatusRecompute(affectedDates);
          return;
        }
        if (!read.verified) {
          // the rows could not be checked against the raw docs — never persist
          // an unverified answer (#4219)
          this.scheduleTaskStatusRecompute(affectedDates);
          return;
        }

        // reconcile with the entries we hold in hand: a day whose Done entry we
        // just saved can never be written back as no_task, even when the view
        // fails to return it (#4212 defect D2)
        const corruptTimeRows: TaskStatusQueryRow[] = [];
        const reconciled = computeTaskStatusesWithSavedEntries(read.rows, entries, minDate, maxDate, today, corruptTimeRows);
        this.reportCorruptTaskStatusRows(corruptTimeRows, TaskStatusReadPhase.Update);
        if (reconciled.suspectDates.length > 0) {
          // NOTE this is a different detector from the view-vs-raw cross-check:
          // it compares the read's answer against the entries we just saved, and
          // it runs on every save. Phase 3's sampling does not change its rate,
          // so it carries no `viewCheck`/`sampleRate` field at all — a reader who
          // scaled this count by the sample rate would overstate it 20-fold.
          //
          // It also does not name the view (#4350). Since Phase 3 the rows judged
          // here come from the raw id-range read on most post-sync reads, so the
          // old name `TaskStatusViewMissedLogDocs` sent a debugger at a subsystem
          // that had not been consulted. `rowsCameFrom` now says which it was.
          this.logging.trackSilentInfo('TaskStatusSavedEntryMissingFromRead', {
            phase: TaskStatusReadPhase.UpdateReconcile,
            suspectDates: reconciled.suspectDates.join(','),
            rowsCameFrom: read.rowsCameFrom,
            // WHICH entries the read did not return, and when each one thinks it
            // is. A log entry's `_id` embeds the time it was CREATED at and is
            // never rewritten, while moving an entry rewrites `time_utc` alone —
            // so an id whose timestamp sits far from its `time_utc` is an entry a
            // read that looks documents up by id range could not have found. That
            // is what roughly 600 earlier reports could not tell anybody.
            missingEntryIds: describeMissingSavedEntries(reconciled.missingEntries, e => e.id),
            missingEntryTimes: describeMissingSavedEntries(reconciled.missingEntries, e => e.time_utc),
            missingEntryCount: reconciled.missingEntries.length,
          });
        }

        // Update the cache with the new statuses
        for (const dateKey of affectedDates) {
          const statuses = reconciled.statuses.get(dateKey);
          if (statuses !== undefined) {
            cache.putDay(dateKey, statuses, today);
          }
        }

        // Save the updated cache
        await this.saveDayTaskStatusCacheAsync(DatabaseWrapper.getTransactionId());

        // Broadcast event so UI components can update
        this.pubSub.publish(TaskStatusCacheUpdatedEvent, {
          startDate: minDate.toISODate(),
          endDate: maxDate.toISODate()
        });

      } catch (error) {
        // must reach a future debugger: a silently failing cache update is how
        // #4212 stayed invisible for weeks
        console.error('Failed to update task status cache:', error);
        this.logging.trackSilentProblem('TaskStatusCacheUpdateFailed', errorReportFields(error));
      }
    });
  }
}
